import { Button, cn, Icon, useTabScopedStorage } from "@qw/ui";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { ChatPosition } from "@/providers/page-provider";
import { usePage } from "@/providers/use-page";

// `z-30` is load-bearing: the nav sits at `z-20` and the draft composer at
// `z-40` (see app-nav.tsx). Alby generating a draft auto-opens that composer,
// so the dock has to stay under it, and out of a Dialog's `z-50` entirely.
const DOCK_CLASSES =
  "backdrop-blur-[2px] ring ring-foreground/25 fixed bottom-4 right-4 rounded-lg shadow-xl z-30 transition-[width] duration-200 ease-in-out motion-reduce:transition-none";

// Expanded is the dock's default; the card is the "get out of my way" state.
// Both heights subtract `--gutter` as the nav's height, which is what leaves
// the nav uncovered — the Alby menu owns the position control, so it has to
// stay reachable.
//
// `w-` is only the starting width: a dragged one overrides it inline. Both
// bounds live here rather than in the drag maths, so CSS is the single
// authority on how wide the dock may be — narrowing the window then never has
// to rewrite the broker's stored preference to make it fit.
const EXPANDED_CLASSES =
  "h-[calc(100dvh-var(--gutter)-2rem)] max-w-[calc(100dvw-2rem)] min-w-112 w-[calc(100dvw-2rem)]";

// The card keeps `h-auto`, so a short conversation still gets a short window.
// The cost is that resizing animates width only: a dock below its height cap
// pops rather than sweeps on the bottom edge.
const MINIMIZED_CLASSES =
  "h-auto max-h-[calc(100dvh-var(--gutter)-2rem)] max-w-[calc(100dvw-var(--gutter)-2rem)] w-112";

/** How far one arrow key moves the edge, for the keyboard path. */
const RESIZE_KEY_STEP_PX = 32;

/** A hand-edited or corrupted key must not reach `style.width`; CSS bounds the rest. */
const readStoredWidth = (value: number | null) =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;

/**
 * The dock's own header controls. Everything the chat header used to carry moved
 * to the Alby menu in the nav (B-2048), but the dock overlays the page, so the
 * nav behind it can neither resize nor close it.
 */
const ChatDockControls = ({
  isMinimized,
  onClose,
  onDockToSide,
  onToggleSize,
}: {
  isMinimized: boolean;
  onClose: () => void;
  onDockToSide: () => void;
  onToggleSize: () => void;
}) => (
  <>
    {/*
     * The same switch the Alby menu's position control makes, within reach of
     * the window it moves: the dock covers the page the broker is reading, and
     * a two-click trip through the nav is a lot to ask for "put this beside it
     * instead". One direction only — the menu is still where the sidebar goes
     * back to a dock, since a sidebar has nothing it needs to get out of.
     */}
    <Button aria-label="Dock to right" variant="ghost" size="sm" display="icon" onClick={onDockToSide}>
      <Icon icon="dock_to_right" />
    </Button>
    <Button
      aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
      variant="ghost"
      size="sm"
      display="icon"
      onClick={onToggleSize}
    >
      <Icon icon={isMinimized ? "open_in_full" : "close_fullscreen"} />
    </Button>
    <Button aria-label="Close chat" variant="ghost" size="sm" display="icon" onClick={onClose}>
      <Icon icon="close" />
    </Button>
  </>
);

/**
 * The dock's left edge, dragged to set its width. A fixed overlay is not a
 * `react-resizable-panels` panel, so it cannot use the handle the sidebar does.
 *
 * Pointer capture rather than window listeners: the browser keeps delivering
 * moves to this element once the gesture starts, so a drag that outruns the
 * cursor needs no global listeners, no cleanup ref and no unmount guard — and
 * touch and pen work for free.
 */
const ChatDockResizeHandle = ({
  onResize,
  onResizeEnd,
}: {
  onResize: (widthPx: number) => void;
  onResizeEnd: () => void;
}) => {
  const lastXRef = useRef<number | null>(null);

  // Each frame measures the dock as it is actually rendered and applies only
  // this frame's delta. Because CSS owns the bounds, dragging past one leaves
  // the measured width sitting at the bound, so the gesture neither runs away
  // nor builds up slack the broker has to drag back through.
  const resizeFrom = (
    event: ReactPointerEvent<HTMLDivElement> | ReactKeyboardEvent<HTMLDivElement>,
    deltaPx: number
  ) => {
    const dock = event.currentTarget.parentElement;

    if (dock) {
      onResize(dock.getBoundingClientRect().width + deltaPx);
    }
  };

  return (
    <div
      aria-label="Resize chat"
      aria-orientation="vertical"
      className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize touch-none hover:bg-foreground/10"
      onKeyDown={(event) => {
        const step =
          event.key === "ArrowLeft" ? RESIZE_KEY_STEP_PX : event.key === "ArrowRight" ? -RESIZE_KEY_STEP_PX : 0;

        if (step !== 0) {
          event.preventDefault();
          resizeFrom(event, step);
          onResizeEnd();
        }
      }}
      onLostPointerCapture={() => {
        lastXRef.current = null;
        onResizeEnd();
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) {
          return;
        }

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        lastXRef.current = event.clientX;
      }}
      onPointerMove={(event) => {
        if (lastXRef.current === null) {
          return;
        }

        // The dock is right-anchored, so dragging the left edge leftward widens it.
        resizeFrom(event, lastXRef.current - event.clientX);
        lastXRef.current = event.clientX;
      }}
      onPointerUp={(event) => event.currentTarget.releasePointerCapture(event.pointerId)}
      role="separator"
      tabIndex={0}
    />
  );
};

/**
 * Everything the dock position adds to a chat column: how wide the window is,
 * the edge that resizes it, and the controls that size and close it. Returns
 * nothing but `undefined` and `null` for every other position, so the column
 * stays a layout component.
 *
 * Call this once per mounted chat. `useTabScopedStorage` does not sync between
 * hook instances, so two callers would disagree about the size. (`useLocalStorage`
 * does sync, via its own `local-storage` event.)
 */
export const useChatDock = () => {
  const { chatPosition, setChatPosition, setIsChatOpen } = usePage();
  // Two tiers on purpose, the same split web makes for its chat sidebar. The
  // size you left it at is per-tab, because minimizing means "get out of my way
  // here"; the width is a standing preference, so it is cross-tab. A `null`
  // width means never dragged, which is what leaves the dock full-width.
  const [isMinimized, setIsMinimized] = useTabScopedStorage("qwibIsChatDockMinimized", false);
  const [storedWidthPx, setStoredWidthPx] = useLocalStorage<number | null>("qwibChatDockWidthPx", null);
  // The live width during a drag. Committing each frame instead would mean a
  // synchronous `localStorage.setItem` and a storage event per pointermove.
  // Non-null doubles as "a drag is in progress".
  const [draggingWidthPx, setDraggingWidthPx] = useState<number | null>(null);
  const isDocked = chatPosition === ChatPosition.dock;
  const widthPx = draggingWidthPx ?? readStoredWidth(storedWidthPx);

  const onToggleSize = useCallback(() => setIsMinimized((minimized) => !minimized), [setIsMinimized]);
  const onClose = useCallback(() => setIsChatOpen(false), [setIsChatOpen]);
  const onDockToSide = useCallback(() => setChatPosition(ChatPosition.side), [setChatPosition]);
  const onResizeEnd = useCallback(
    () =>
      setDraggingWidthPx((current) => {
        if (current !== null) {
          setStoredWidthPx(current);
        }

        return null;
      }),
    [setStoredWidthPx]
  );

  // Memoized apart from the width, which changes on every frame of a drag, so
  // the chat header is not handed a new `actions` node on each one.
  const headerActions = useMemo(
    () =>
      isDocked ? (
        <ChatDockControls
          isMinimized={isMinimized}
          onClose={onClose}
          onDockToSide={onDockToSide}
          onToggleSize={onToggleSize}
        />
      ) : null,
    [isDocked, isMinimized, onClose, onDockToSide, onToggleSize]
  );

  // Only the expanded dock resizes. The card is a fixed chip, and the control
  // for wanting something other than its size is the expand button.
  const resizeHandle = useMemo(
    () =>
      isDocked && !isMinimized ? (
        <ChatDockResizeHandle onResize={setDraggingWidthPx} onResizeEnd={onResizeEnd} />
      ) : null,
    [isDocked, isMinimized, onResizeEnd]
  );

  return useMemo(() => {
    if (!isDocked) {
      return { className: undefined, headerActions, headerClassName: undefined, resizeHandle, style: undefined };
    }

    return {
      // A transition would leave a drag one frame behind the cursor. The card's
      // width is a class, so only the expanded dock carries an inline width.
      className: cn(
        DOCK_CLASSES,
        isMinimized ? MINIMIZED_CLASSES : EXPANDED_CLASSES,
        draggingWidthPx !== null && "transition-none"
      ),
      headerActions,
      headerClassName: "h-12",
      resizeHandle,
      style: isMinimized || widthPx === null ? undefined : { width: widthPx },
    };
  }, [draggingWidthPx, headerActions, isDocked, isMinimized, resizeHandle, widthPx]);
};
